import {
  Apple,
  Play,
  Home,
  Search,
  PlusCircle,
  MessageCircle,
  User,
  MapPin,
  BadgeCheck,
  Smartphone,
} from "lucide-react";
import { Reveal } from "./Reveal";

const PhoneJobs = () => (
  <div className="w-[240px] sm:w-[270px] h-[500px] sm:h-[540px] rounded-[2.6rem] bg-navy-dark p-2.5 shadow-2xl shadow-navy/40 rotate-[-4deg]">
    <div className="w-full h-full rounded-[2.1rem] bg-paper overflow-hidden flex flex-col">
      <div className="bg-navy px-5 pt-5 pb-6">
        <div className="w-16 h-1.5 rounded-full bg-white/20 mx-auto mb-4" />
        <p className="text-white/60 text-[11px]">Good morning</p>
        <p className="text-white font-display font-bold text-lg">Alex</p>
        <div className="mt-3 bg-white/10 rounded-full px-4 py-2.5 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-white/50" />
          <span className="text-white/50 text-xs">What do you need done?</span>
        </div>
      </div>
      <div className="flex-1 p-4 space-y-3">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Nearby jobs</p>
        {[
          { t: "Furniture Assembly", loc: "NW London", p: "£70" },
          { t: "TV Mounting", loc: "Camden", p: "£55" },
          { t: "Garden Tidy-Up", loc: "Islington", p: "£90" },
        ].map((j) => (
          <div key={j.t} className="bg-white rounded-xl border border-slate-200/70 p-3.5 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-navy">{j.t}</p>
              <p className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                <MapPin className="w-2.5 h-2.5 text-teal" /> {j.loc}
              </p>
            </div>
            <span className="text-xs font-display font-extrabold text-teal">{j.p}</span>
          </div>
        ))}
      </div>
      <div className="bg-white border-t border-slate-100 px-6 py-3.5 flex justify-between">
        <Home className="w-5 h-5 text-teal" />
        <Search className="w-5 h-5 text-slate-300" />
        <PlusCircle className="w-5 h-5 text-slate-300" />
        <MessageCircle className="w-5 h-5 text-slate-300" />
        <User className="w-5 h-5 text-slate-300" />
      </div>
    </div>
  </div>
);

const PhoneChat = () => (
  <div className="w-[240px] sm:w-[270px] h-[500px] sm:h-[540px] rounded-[2.6rem] bg-navy-dark p-2.5 shadow-2xl shadow-navy/40 rotate-[4deg] mt-10 sm:mt-16">
    <div className="w-full h-full rounded-[2.1rem] bg-white overflow-hidden flex flex-col">
      <div className="bg-white border-b border-slate-100 px-5 pt-5 pb-4">
        <div className="w-16 h-1.5 rounded-full bg-slate-200 mx-auto mb-4" />
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-teal grid place-items-center text-[11px] font-bold text-white">SK</span>
          <div>
            <p className="text-sm font-bold text-navy flex items-center gap-1">
              Sofia K. <BadgeCheck className="w-3.5 h-3.5 text-teal" />
            </p>
            <p className="text-[10px] text-slate-400">Tasker · 4.9 ★ · 132 jobs</p>
          </div>
        </div>
      </div>
      <div className="flex-1 p-4 space-y-2.5">
        <div className="max-w-[75%] bg-paper rounded-2xl rounded-tl-md px-3.5 py-2.5 text-xs text-navy">
          Hi! I can build your wardrobe tomorrow morning if that works?
        </div>
        <div className="max-w-[75%] ml-auto bg-navy rounded-2xl rounded-tr-md px-3.5 py-2.5 text-xs text-white">
          Perfect — 9am?
        </div>
        <div className="bg-teal/[0.07] border border-teal/20 rounded-2xl p-3.5 mt-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-teal">Offer received</p>
          <div className="flex items-center justify-between mt-1.5">
            <span className="font-display font-extrabold text-navy text-xl">£68</span>
            <span className="text-[10px] font-bold bg-teal text-navy px-3 py-1.5 rounded-full">Accept</span>
          </div>
        </div>
      </div>
      <div className="px-4 pb-5">
        <div className="bg-paper rounded-full px-4 py-2.5 text-xs text-slate-400">Message…</div>
      </div>
    </div>
  </div>
);

export default function AppSection() {
  return (
    <section id="app" data-testid="app-section" className="relative bg-navy py-20 md:py-28 overflow-hidden">
      <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-teal/10 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative">
        <Reveal>
          <span
            data-testid="app-platforms-badge"
            className="inline-flex items-center gap-2 text-xs font-mono font-medium uppercase tracking-[0.2em] text-teal-light bg-teal/10 border border-teal/25 px-4 py-2 rounded-full"
          >
            <Smartphone className="w-3.5 h-3.5" /> iOS & Android
          </span>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
            Your jobs. Your phone. Your marketplace.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-md">
            Post jobs, find work, chat with customers and manage everything from the app.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              data-testid="app-store-badge"
              className="inline-flex items-center gap-3 bg-white text-navy px-6 py-3 rounded-2xl hover:bg-slate-100 transition-colors duration-200"
            >
              <Apple className="w-7 h-7" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] text-slate-500">Download on the</span>
                <span className="block font-display font-bold">App Store</span>
              </span>
            </button>
            <button
              type="button"
              data-testid="google-play-badge"
              className="inline-flex items-center gap-3 bg-white text-navy px-6 py-3 rounded-2xl hover:bg-slate-100 transition-colors duration-200"
            >
              <Play className="w-6 h-6" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] text-slate-500">Get it on</span>
                <span className="block font-display font-bold">Google Play</span>
              </span>
            </button>
          </div>

          <p className="mt-6 text-sm text-slate-400">Free to download. Free to post a job.</p>
        </Reveal>

        <Reveal delay={0.2} className="flex justify-center items-start gap-4 sm:gap-6">
          <PhoneJobs />
          <PhoneChat />
        </Reveal>
      </div>
    </section>
  );
}
